import React, { useEffect, useMemo, useState } from 'react'
import styles from '../llmSettingsTab/LlmSettingsTab.module.css'
import systemStyles from '../systemSettingsTab/SystemSettingsTab.module.css'
import { settingsApi } from '../../../lib/apiClient'
import { toast } from '../../../lib/toast'
import { FieldMeta, SettingsMap } from '../../../types/webchatTabsTypes'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import { hasValue } from '../settingsUtils'
import { useAuthStore } from '../../../store/authStore'
import { useSettingsStore } from '../../../store/settingsStore'

const SECTIONS: { heading: string; fields: FieldMeta[] }[] = [
  {
    heading: 'Agent Loop',
    fields: [
      {
        key: 'ai_builder_max_iterations',
        label: 'Max Agent Iterations',
        description: 'Maximum generate → validate → test cycles before the agent stops and blocks.',
        type: 'number',
        fallbackDefault: 10,
        min: 1,
        step: 1,
      },
      {
        key: 'ai_builder_stuck_threshold',
        label: 'Stuck Threshold',
        description: 'Consecutive same-category errors before the agent asks the user for clarification.',
        type: 'number',
        fallbackDefault: 5,
        min: 1,
        step: 1,
      },
      {
        key: 'ai_builder_test_timeout_ms',
        label: ' Test Timeout (ms)',
        description: 'Max time (ms) the agent waits for a single tool-test call (auth + request) before giving up and reporting the test as failed.',
        type: 'number',
        fallbackDefault: 60000,
        min: 1000,
        step: 1000,
      },
    ],
  },
  {
    heading: 'Display & Truncation',
    fields: [
      {
        key: 'ai_builder_activity_success_chars',
        label: 'Activity Log - Success Preview Chars',
        description: 'Max characters of a success response shown in the chat activity log (frontend).',
        type: 'number',
        fallbackDefault: 400,
        min: 100,
        step: 100,
      },
      {
        key: 'ai_builder_activity_error_chars',
        label: 'Activity Log - Error Preview Chars',
        description: 'Max characters of an error response shown in the chat activity log (frontend).',
        type: 'number',
        fallbackDefault: 600,
        min: 100,
        step: 100,
      },
      {
        key: 'ai_builder_test_success_chars',
        label: 'Backend - Test Success Preview Chars',
        description: 'Max characters of a success response returned by the backend tool-test endpoint.',
        type: 'number',
        fallbackDefault: 800,
        min: 100,
        step: 100,
      },
      {
        key: 'ai_builder_test_error_chars',
        label: 'Backend - Test Error Preview Chars',
        description: 'Max characters of an error response returned by the backend tool-test endpoint.',
        type: 'number',
        fallbackDefault: 600,
        min: 100,
        step: 100,
      },
    ],
  },
  {
    heading: 'Debug',
    fields: [
      {
        key: 'ai_builder_debug_mode',
        label: 'AI Debug Mode',
        description: 'When enabled, a debug icon appears in the AI Builder chat panel. Clicking it reveals the exact prompt sent to the AI and its raw response for each agent loop step.',
        type: 'boolean',
        fallbackDefault: 'false',
        superAdminOnly: true,
        note: 'Visible and operable by Super Admins only. Debug data is session-only and never saved.',
      },
    ],
  },
]

const ALL_FIELDS: FieldMeta[] = SECTIONS.flatMap(s => s.fields)

const normalizeResponse = (raw: unknown): SettingsMap => {
  if (Array.isArray(raw)) {
    const mapped: SettingsMap = {}
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue
      const rec = item as Record<string, unknown>
      const key = typeof rec.key === 'string' ? rec.key : ''
      if (!key) continue
      mapped[key] = {
        value: (rec.value as string | number) ?? '',
        default: (rec.default as string | number) ?? (rec.defaultValue as string | number) ?? '',
        isSensitive: typeof rec.isSensitive === 'boolean' ? rec.isSensitive : undefined,
      }
    }
    return mapped
  }
  if (raw && typeof raw === 'object') {
    const container = raw as Record<string, unknown>
    if (Array.isArray(container.settings)) return normalizeResponse(container.settings)
    if (Array.isArray(container.data)) return normalizeResponse(container.data)
  }
  return {}
}

const AiBuilderSettingsTab = () => {
  const [settings, setSettings] = useState<SettingsMap | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [initialDraft, setInitialDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [debugToggling, setDebugToggling] = useState(false)

  const { user } = useAuthStore()
  const isSuperAdmin = user?.role?.id === 1

  const handleDebugToggle = async (key: string) => {
    setDebugToggling(true)
    try {
      const current = settings?.[key]?.value
      const nextValue = String(current !== 'true')
      await settingsApi.setValue(key as any, { value: nextValue })
      await loadSettings(false)
      useSettingsStore.getState().setResolvedValue(key, nextValue)
    } catch {
      toast.error('Failed to update debug mode')
    } finally {
      setDebugToggling(false)
    }
  }

  const loadSettings = async (showErrorToast = true) => {
    try {
      const raw = await settingsApi.getAiBuilderSettings()
      const fromServer = normalizeResponse(raw)
      const normalized: SettingsMap = { ...fromServer }
      const initial: Record<string, string> = {}

      for (const { key, fallbackDefault } of ALL_FIELDS) {
        const rawValue = fromServer[key]?.value
        const rawDefault = fromServer[key]?.default
        const def = hasValue(rawDefault) ? (rawDefault as string | number) : fallbackDefault
        const value = hasValue(rawValue) ? (rawValue as string | number) : def
        normalized[key] = { value, default: def }
        initial[key] = String(value)
      }

      setSettings(normalized)
      setDraft(initial)
      setInitialDraft(initial)
    } catch {
      if (showErrorToast) toast.error('Failed to load AI Builder settings')
    }
  }

  useEffect(() => {
    loadSettings().finally(() => setLoading(false))
  }, [])

  const isDirty = useMemo(
    () => ALL_FIELDS.some(({ key }) => draft[key] !== (initialDraft[key] ?? '')),
    [draft, initialDraft],
  )

  const handleSaveAll = async (e: React.FormEvent) => {
    e.preventDefault()
    for (const { key, label, min, type } of ALL_FIELDS) {
      if (type === 'boolean') continue
      const num = Number(draft[key]?.trim())
      if (isNaN(num)) { toast.error(`${label}: please enter a valid number`); return }
      if (min !== undefined && num < min) { toast.error(`${label}: must be >= ${min}`); return }
    }
    setSaving(true)
    try {
      const updates: Record<string, string> = {}
      for (const { key, type } of ALL_FIELDS) {
        if (type === 'boolean') continue
        const raw = draft[key]?.trim() ?? ''
        if (raw === (initialDraft[key] ?? '')) continue
        updates[key] = String(Number(raw))
      }
      if (Object.keys(updates).length === 0) {
        toast.success('No changes to save')
        setSaving(false)
        return
      }
      await Promise.all(Object.entries(updates).map(([key, value]) => settingsApi.setValue(key, { value })))
      await loadSettings(false)
      toast.success('AI Builder settings saved')
    } catch {
      toast.error('Failed to save AI Builder settings')
    } finally {
      setSaving(false)
    }
  }

  const handleResetAll = async () => {
    if (!settings) return
    setSaving(true)
    try {
      const updates = Object.fromEntries(
        ALL_FIELDS
          .filter(({ type }) => type !== 'boolean')
          .map(({ key, fallbackDefault }) => [key, String(settings[key]?.default ?? fallbackDefault)])
      )
      await Promise.all(Object.entries(updates).map(([key, value]) => settingsApi.setValue(key, { value })))
      await loadSettings(false)
      toast.success('Reset to defaults')
    } catch {
      toast.error('Failed to reset settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className={styles.loading}>Loading…</div>
  if (!settings) return null

  return (
    <div className={styles.tab}>
      <div className={styles.header}>
        <div>
          <h2>AI Chat Tool Builder Settings</h2>
          <p>Configure the agent loop behaviour and response truncation limits.</p>
        </div>
      </div>

      <form className={styles.form} onSubmit={handleSaveAll}>
        {SECTIONS.map(({ heading, fields }) => {
          const numericFields = fields.filter(f => f.type !== 'boolean')
          if (numericFields.length === 0) return null
          return (
            <React.Fragment key={heading}>
              <div className={styles.sectionHeading}>
                <h3 className={styles.sectionTitle}>{heading}</h3>
              </div>
              {numericFields.map(({ key, label, description, min, step }) => (
                <div key={key} className={styles.fieldRow}>
                  <div className={styles.fieldHeader}>
                    <span className={styles.fieldLabel}>{label}</span>
                    <span className={styles.keyBadge}>{key}</span>
                  </div>
                  <p className={styles.fieldDescription}>{description}</p>
                  <div className={styles.fieldInputRow}>
                    <div className={styles.fieldInputControls}>
                      <SecureInput
                        fieldType="number"
                        value={draft[key] ?? ''}
                        min={min}
                        step={step ?? 1}
                        onChange={(value: string) => setDraft(d => ({ ...d, [key]: value }))}
                      />
                    </div>
                    <span className={styles.defaultHint}>default: {settings[key]?.default}</span>
                  </div>
                </div>
              ))}
            </React.Fragment>
          )
        })}

        <div className={styles.footer}>
          <button type="submit" className={styles.saveBtn} disabled={!isDirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className={styles.resetBtn} disabled={saving} onClick={handleResetAll}>
            Reset all to defaults
          </button>
        </div>
      </form>

      {SECTIONS.map(({ heading, fields }) => {
        const boolFields = fields.filter(f => f.type === 'boolean' && (!f.superAdminOnly || isSuperAdmin))
        if (boolFields.length === 0) return null
        return (
          <React.Fragment key={`bool-${heading}`}>
            <div className={styles.sectionHeading} style={{ marginTop: '24px' }}>
              <h3 className={styles.sectionTitle}>{heading}</h3>
            </div>
            {boolFields.map(({ key, label, description, note }) => {
              const isEnabled = settings[key]?.value === 'true'
              return (
                <div key={key} className={systemStyles.settingRow}>
                  <div className={systemStyles.settingInfo}>
                    <h3 className={systemStyles.settingLabel}>{label}</h3>
                    <p className={systemStyles.settingDescription}>{description}</p>
                    {note && <p className={systemStyles.settingNote}>{note}</p>}
                  </div>
                  <div className={systemStyles.settingControl}>
                    <button
                      className={`${systemStyles.toggleBtn} ${isEnabled ? systemStyles.toggleOn : systemStyles.toggleOff}`}
                      onClick={() => handleDebugToggle(key)}
                      disabled={!settings[key] || debugToggling}
                      title={isEnabled ? 'Click to disable debug mode' : 'Click to enable debug mode'}
                    >
                      <span className={systemStyles.toggleKnob} />
                    </button>
                    <div className={`${systemStyles.statusLabel} ${isEnabled ? systemStyles.statusOn : systemStyles.statusOff}`}>
                      {isEnabled ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              )
            })}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default AiBuilderSettingsTab
